import { makeAutoObservable, runInAction } from 'mobx';
import { deleteEnrollments, triggerAllRenewal, triggerRenewalsBatch } from 'api/operations/enrollmentService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { generateExhibits } from 'api/operations/proposalServiceV3';
import { sendRenewalDigest } from 'api/operations/groupService';
import { getRootStoreInstance } from 'store/rootStore';
import type { TriggerRenewalsPaginationDelegate } from './pageStore';
import { TriggerModalType, TriggerRenewalsPeriod } from './types';

const NO_SUITABLE_RENEWALS_MESSAGE =
  'No suitable renewals were found for triggering. Please verify if the exhibits have been provided.';
const RENEWALS_TRIGGERED_MESSAGE = 'Renewals were successfully triggered.';
const STAGES_CHANGED_MESSAGE = 'Stages were successfully changed to Renewal.';

async function triggerAllRenewals(
  sponsorId: string,
  renewalEffectiveDate: string,
  sendEmails: boolean,
): Promise<boolean> {
  const res = await triggerAllRenewal(sponsorId, renewalEffectiveDate, sendEmails);
  return res.data?.triggerAllRenewal ?? false;
}

interface BatchActionDelegateParams {
  paginationDelegate: TriggerRenewalsPaginationDelegate;
  getAllPeriods: () => TriggerRenewalsPeriod[];
  reloadPage: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

export class BatchActionDelegate {
  private _rootStore = getRootStoreInstance();
  private readonly _paginationDelegate: TriggerRenewalsPaginationDelegate;
  private readonly _getAllPeriods: () => TriggerRenewalsPeriod[];
  private readonly _reloadPage: () => Promise<void>;
  private readonly _setLoading: (loading: boolean) => void;
  private _selectedPeriods: TriggerRenewalsPeriod[] = [];
  private _selectedGroupIds: string[] = [];
  private _isSelectAllButtonChecked = false;
  private _warningModalType: TriggerModalType | null = null;

  public get selectedPeriods(): TriggerRenewalsPeriod[] {
    return this._selectedPeriods;
  }

  public get selectedGroupIds(): string[] {
    return this._selectedGroupIds;
  }

  public get selectedPeriodIds(): string[] {
    return this._selectedPeriods.map((period) => period.id);
  }

  public get isSelectAllButtonChecked(): boolean {
    return this._isSelectAllButtonChecked;
  }

  public get warningModalType(): TriggerModalType | null {
    return this._warningModalType;
  }

  public get isWarningModalOpen(): boolean {
    return this._warningModalType !== null;
  }

  public get selectedPeriodsCanBeRenewed(): boolean {
    return (
      this._selectedPeriods.length > 0 &&
      this._selectedPeriods.every((period) => period.hasAllExhibits && period.hasAllRates)
    );
  }

  public isPeriodSelected(periodId: string): boolean {
    return this._selectedPeriods.some((selectedPeriod) => selectedPeriod.id === periodId);
  }

  public selectPeriod(period: TriggerRenewalsPeriod, selectedGroupId: string): void {
    if (this.isPeriodSelected(period.id)) {
      return;
    }

    this._selectedPeriods = [...this._selectedPeriods, period];

    if (!this._selectedGroupIds.some((id) => id === selectedGroupId)) {
      this._selectedGroupIds = [...this._selectedGroupIds, selectedGroupId];
    }
  }

  public deselectPeriod(period: TriggerRenewalsPeriod): void {
    this._selectedPeriods = this._selectedPeriods.filter((selectedPeriod) => selectedPeriod.id !== period.id);

    const hasRemainingPeriodsInGroup = this._selectedPeriods.some(
      (selectedPeriod) => selectedPeriod.groupId === period.groupId,
    );

    if (!hasRemainingPeriodsInGroup) {
      this._selectedGroupIds = this._selectedGroupIds.filter((id) => id !== period.groupId);
    }
  }

  public updateSelectedPeriodsList(period: TriggerRenewalsPeriod, selectedGroupId: string): void {
    if (this.isPeriodSelected(period.id)) {
      this.deselectPeriod(period);
      return;
    }

    this.selectPeriod(period, selectedGroupId);
  }

  public resetSelection(): void {
    this._selectedPeriods = [];
    this._selectedGroupIds = [];
    this._isSelectAllButtonChecked = false;
    this._warningModalType = null;
  }

  public setSelectAllButtonStatus(): void {
    this._isSelectAllButtonChecked = !this.isSelectAllButtonChecked;

    if (this.isSelectAllButtonChecked) {
      this._selectedPeriods = this._getAllPeriods();
    } else {
      this._selectedPeriods = [];
    }
  }

  public openWarningModal(modalType: TriggerModalType): void {
    this._warningModalType = modalType;
  }

  public closeWarningModal(): void {
    this._warningModalType = null;
  }

  public async deleteSelectedEnrollments(): Promise<void> {
    this._setLoading(true);
    try {
      await deleteEnrollments(this.selectedPeriodIds);
      toastManager.addToast('Periods were successfully removed', ToastColorTypes.SUCCESS);
      await this._reloadPage();
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
    } finally {
      runInAction(() => {
        this._setLoading(false);
      });
    }
  }

  public async moveSelectedPeriodsToRenewals(): Promise<void> {
    await this._triggerRenewals(() => triggerRenewalsBatch(this.selectedPeriodIds, false), STAGES_CHANGED_MESSAGE);
  }

  public async triggerSelectedRenewals(): Promise<void> {
    await this._triggerRenewals(() => triggerRenewalsBatch(this.selectedPeriodIds, true), RENEWALS_TRIGGERED_MESSAGE);
  }

  public async triggerAllRenewal(): Promise<void> {
    const { effectiveDate } = this._paginationDelegate.filters;
    await this._triggerRenewals(
      () => triggerAllRenewals(this._rootStore.userStore.sponsorId, effectiveDate, true),
      RENEWALS_TRIGGERED_MESSAGE,
    );
  }

  public async moveAllToRenewal(): Promise<void> {
    const { effectiveDate } = this._paginationDelegate.filters;
    await this._triggerRenewals(
      () => triggerAllRenewals(this._rootStore.userStore.sponsorId, effectiveDate, false),
      STAGES_CHANGED_MESSAGE,
    );
  }

  private async _triggerRenewals(trigger: () => Promise<boolean>, successMessage: string): Promise<void> {
    this._setLoading(true);
    try {
      const wereRenewalsTriggered = await trigger();
      if (wereRenewalsTriggered) {
        toastManager.addToast(successMessage, ToastColorTypes.SUCCESS);
      } else {
        toastManager.addToast(NO_SUITABLE_RENEWALS_MESSAGE, ToastColorTypes.WARNING);
      }

      void this._reloadPage();
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
    } finally {
      runInAction(() => {
        this._setLoading(false);
      });
    }
  }

  public generateExhibits(): void {
    // The request may take a while, so we show an in-progress toast immediately instead of waiting for the response.
    void generateExhibits(this._rootStore.userStore.sponsorId, this._paginationDelegate.filters.effectiveDate);
    toastManager.addToast('Exhibits generation in progress', ToastColorTypes.SUCCESS);
  }

  public async sendRenewalDigest(): Promise<void> {
    const { effectiveDate } = this._paginationDelegate.filters;
    this._setLoading(true);
    try {
      await sendRenewalDigest(this._rootStore.userStore.sponsorId, effectiveDate);
      toastManager.addToast('Statistics was successfully sent.', ToastColorTypes.SUCCESS);
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
    } finally {
      runInAction(() => {
        this._setLoading(false);
      });
    }
  }

  constructor({ paginationDelegate, getAllPeriods, reloadPage, setLoading }: BatchActionDelegateParams) {
    this._paginationDelegate = paginationDelegate;
    this._getAllPeriods = getAllPeriods;
    this._reloadPage = reloadPage;
    this._setLoading = setLoading;
    makeAutoObservable<BatchActionDelegate, '_paginationDelegate' | '_getAllPeriods' | '_reloadPage' | '_setLoading'>(
      this,
      {
        _paginationDelegate: false,
        _getAllPeriods: false,
        _reloadPage: false,
        _setLoading: false,
      },
      { autoBind: true },
    );
  }
}
